import React, { useState } from 'react';
import './corporate-action-payment-report-container.scss';
import UploadModalContent from '../modal/upload-modal/upload-modal';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import ReportsService from '../../../../../../services/reports-service';
import { ExcelPreviewModal } from '../modal/excel-preview-modal/excel-preview-modal'; // Add ExcelPreviewModal import
import * as XLSX from 'xlsx'; // Import XLSX for parsing Excel files

const CorporateActionPaymentReportContainer = ({ filteredReports = [], handleCheckboxChange }) => {
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: '', direction: '' });
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [selectedComment, setSelectedComment] = useState('');
  
  const [excelPreviewData, setExcelPreviewData] = useState([]); // New state for Excel preview data
  const [showExcelPreviewModal, setShowExcelPreviewModal] = useState(false); // New state to control Excel preview modal
  
  const reportService = new ReportsService(); 

  const handleCommentClick = (comment) => {
    setSelectedComment(comment);
    setShowCommentModal(true);
  };

  const handleUploadClick = (report) => {
    setSelectedReport(report);
    setShowUploadModal(true);
  };

  const handleCloseModal = () => {
    setShowUploadModal(false);
    setSelectedReport(null);
  };

  const handleSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const sortedReports = [...filteredReports].sort((a, b) => {
    if (sortConfig.key) {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      // Convert string dates to Date objects for comparison
      if (sortConfig.key === 'paymentDate' || sortConfig.key === 'dateSubmitted') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'ascending' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'ascending' ? 1 : -1;
      }
    }
    return 0;
  });

  // Helper function to return an embed URL for XLSX files using Office Web Viewer
  const getEmbedUrl = (url) => {
    try {
      const urlObj = new URL(url);
      const pathname = urlObj.pathname.toLowerCase();
      if (pathname.endsWith('.xlsx')) {
        return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`;
      }
    } catch (e) {
      // Fallback: strip query parameters manually
      const strippedUrl = url.split('?')[0].toLowerCase();
      if (strippedUrl.endsWith('.xlsx')) {
        return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`;
      }
    }
    return url;
  };

  // Handle the preview action: open the URL with the proper embed link if it's an XLSX file.
  const handlePreviewClick = async (report) => {
    try {
      const response = await reportService.retrieveReport(report.type, report.isin, report.title, report.paymentDate);
      const fileURL = response.fileURL;

      const urlObj = new URL(fileURL);
      // Check if the file is an Excel file
      if (urlObj.pathname.toLowerCase().endsWith('.xlsx')) {
        const fileResponse = await fetch(fileURL);
        const blob = await fileResponse.blob();
        const arrayBuffer = await blob.arrayBuffer();
        const workbook = XLSX.read(arrayBuffer, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(sheet, { header: 1 });
        const spreadsheetData = jsonData.map(row => row.map(cell => ({ value: cell, readOnly: true })));
        setExcelPreviewData(spreadsheetData);
        setShowExcelPreviewModal(true);
      } else {
        const embedUrl = getEmbedUrl(fileURL);
        const newWindow = window.open(embedUrl, '_blank', 'noopener,noreferrer');
        if (newWindow) newWindow.opener = null;
      }
    } catch (error) {
      console.error('Error fetching report preview:', error.message);
    }
  };

  // Handle the download action remains unchanged
  const handleDownloadClick = async (report) => {
    try {
      const response = await reportService.retrieveReport(report.type, report.isin, report.title, report.paymentDate);
      
      const fileResponse = await fetch(response.fileURL);
      if (!fileResponse.ok) throw new Error('Network response was not ok');

      const blob = await fileResponse.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      
      // Use the file name provided by the server or a fallback value
      const fileName = response.fileName || 'download.pdf';
      link.href = objectUrl;
      link.download = fileName;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(objectUrl);
    } catch (error) {
      console.error('Error downloading report:', error.message);
    }
  };

  return (
    <div className="table-container">
      <table className="report-table">
        <thead>
          <tr>
            <th className="checkbox-header"></th>
            <th>Report Title</th>
            <th>Issuer</th>
            <th>ISIN</th>
            <th onClick={() => handleSort('paymentDate')}>
              Payment Date {sortConfig.key === 'paymentDate' && (sortConfig.direction === 'ascending' ? '▲' : '▼')}
            </th>
            <th>Status</th>
            <th onClick={() => handleSort('dateSubmitted')}>
              Date Submitted {sortConfig.key === 'dateSubmitted' && (sortConfig.direction === 'ascending' ? '▲' : '▼')}
            </th>
            <th>Issuer Receipt Date</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {sortedReports.map(report => (
            <tr key={report.id}>
              <td className="checkbox-cell">
                {report.status === 'For Checking' ? (
                  <input 
                    type="checkbox" 
                    onChange={() => handleCheckboxChange(report.id)} 
                  />
                ) : null}
              </td>
              <td>{report.title}</td>
              <td>{report.issuer}</td>
              <td>{report.isin}</td>
              <td>{report.paymentDate}</td>
              <td className={`status ${report.status.replace(' ', '-').toLowerCase()}`}> 
                {report.status} 
                {report.comment && (
                  <span
                  style={{ cursor: 'pointer', marginLeft: '5px' }}
                  onClick={() => handleCommentClick(report.comment)}
                >
                  &#9865;
                </span>
                )}
              </td>
              <td>{report.dateSubmitted}</td>
              <td>{report.issuerReceiptDate}</td>
              <td>
                <div className="action-buttons">
                  <Button variant="primary" className="action-button" onClick={() => handlePreviewClick(report)}>Preview</Button>
                  <Button variant="primary" className="action-button" onClick={() => handleDownloadClick(report)}>Download</Button>
                  {report.status === 'For Checking' && (
                    <Button variant="primary" className="action-button" onClick={() => handleUploadClick(report)}>Upload</Button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Render the Upload Modal */}
      {selectedReport && (
        <Modal show={showUploadModal} onHide={handleCloseModal} dialogClassName='checker-upload-modal'>
          <Modal.Header closeButton>
            <Modal.Title>Upload Document</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <UploadModalContent context={{ report: selectedReport }} onClose={handleCloseModal} />
          </Modal.Body>
        </Modal>
      )}

      {/* Comment Modal */}
      <Modal show={showCommentModal} onHide={() => setShowCommentModal(false)} backdrop="static" dialogClassName="commentModal modal-dialog-centered">
        <Modal.Header closeButton>
          <Modal.Title>Report Comment</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{selectedComment || 'No Comments'}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCommentModal(false)}>Close</Button>
        </Modal.Footer>
      </Modal>

      {/* Excel Preview Modal */}
      {showExcelPreviewModal && (
        <ExcelPreviewModal
          show={showExcelPreviewModal}
          data={excelPreviewData}
          onClose={() => setShowExcelPreviewModal(false)}
        />
      )}
    </div>
  );
};

export default CorporateActionPaymentReportContainer;
